import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form@latest';
import cn from 'classnames';

import { _notNil } from '@/littledash';
import { ONLY_DIGITS_REGEX } from '@/validators.constants';

import { FormField } from '@/components/UI/FormFields/Latest';
import { getRegisterConfig } from '@/components/UI/FormFields/FormField.utils';

interface DigitsOnlyFieldProps {
  value?: string;
  name: string;
  label: string;
  disabled?: boolean;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  testId?: string;
}

const DigitsOnlyField: FC<DigitsOnlyFieldProps> = ({
  value,
  name,
  label,
  disabled = false,
  hint,
  required,
  placeholder,
  maxLength,
  autoFocus,
  testId = 'digits-only-field',
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();

  const rules = getRegisterConfig({
    required,
    pattern: {
      value: ONLY_DIGITS_REGEX,
      message: 'Must contain only digits',
    },
    maxLength,
  });

  const hasError = _notNil(errors[name]);

  return (
    <FormField label={label} name={name} hint={hint} required={required} testPrefix={testId}>
      <div data-test-component="DigitsOnlyField">
        <Controller
          name={name}
          control={control}
          defaultValue={value ?? ''}
          rules={rules}
          render={({ field: { onChange, value: fieldValue } }) => {
            const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
              onChange(event.target.value);
              trigger(name);
            };

            return (
              <input
                id={name}
                type="text"
                inputMode="numeric"
                onChange={handleChange}
                value={fieldValue}
                className={cn({ input__error: hasError })}
                disabled={disabled}
                style={{ marginBottom: 0 }}
                placeholder={placeholder}
                autoFocus={autoFocus}
                data-test-element="input"
                data-testid={testId}
              />
            );
          }}
        />
      </div>
    </FormField>
  );
};

export default DigitsOnlyField;
